import dynamic from 'next/dynamic';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import SearchSuggestionSelectionType from '@alltrails/amplitude/enums/SearchSuggestionSelectionType';
import SearchTabLocation from '@alltrails/amplitude/enums/SearchTabLocation';
import logExploreSearchBoxClicked from '@alltrails/amplitude/events/logExploreSearchBoxClicked';
import logGuideClicked from '@alltrails/amplitude/events/logGuideClicked';
import logGuideIndexClicked from '@alltrails/amplitude/events/logGuideIndexClicked';
import logSearchResultClicked from '@alltrails/amplitude/events/logSearchResultClicked';
import logSearchTabSwitched from '@alltrails/amplitude/events/logSearchTabSwitched';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import logError from '@alltrails/core/utils/logError';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments } from '@alltrails/experiments';
import type { AlgoliaResultType, SearchType } from '@alltrails/types/Algolia';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type {
  AlgoliaHit,
  AlgoliaSearchResult,
  CoordinatesResult,
  GuideMetadata,
  NearbyResult,
  SponsoredListHit,
  StreetAddressResult
} from '@alltrails/types/AlgoliaResults';
import type Context from '@alltrails/types/Context';
import useAlgoliaIndex from '../../hooks/useAlgoliaIndex';
import useRecentAlgoliaSearches from '../../hooks/useRecentAlgoliaSearches';
import { SuggestionsParams, useLazyGetSuggestionsQuery } from '../../redux/suggestionsApi';
import type { SearchRequestOptions } from '../../types/algoliaResultTypes';
import { OnSelect, Results, SearchResultsEmptyState, SearchResultsTabBar } from '../../types/searchBoxTypes';
import { getAnalyticsTags, getSearchItemType, getSearchSelectionType } from '../../utils/algoliaAnalyticsHelpers';
import { algoliaHitToResult, getUrl, guideMetadataToSearchResult } from '../../utils/algoliaResultUtils';
import getStreetAddressResults from '../../utils/getStreetAddressResults';
import AlgoliaSearchResultContent from '../AlgoliaSearchResultContent/AlgoliaSearchResultContent';
import CoordinatesResultContent from '../AlgoliaSearchResultContent/CoordinatesResultContent';
import NearbyResultContent from '../AlgoliaSearchResultContent/NearbyResultContent';
import NLSearchResultContent from '../AlgoliaSearchResultContent/NLSearchResultContent';
import StreetAddressResultContent from '../AlgoliaSearchResultContent/StreetAddressResultContent';
import TrailResultContent from '../AlgoliaSearchResultContent/TrailResultContent';
import CustomSearchBox, { CustomSearchBoxProps } from '../CustomSearchBox/CustomSearchBox';
import NLSearchSuggestions from './NLSearchSuggestions';
import reorderResults from './reorderResults';
import styles from './styles/styles.module.scss';
import { Hit, Result } from './types';

const AlgoliaGuidesCarousel = dynamic(() => import('./AlgoliaGuidesCarousel'));
const SponsoredListsCarousel = dynamic(() => import('../SponsoredListsCarousel/SponsoredListsCarousel'));

const locationTabId = 'location';
const streetAddressTabId = 'street-address';

// https://github.com/alltrails/explore-service/blob/alpha/app/contracts/suggestions/default_values.rb#L7
export const defaultRecordTypes: SuggestionsParams['recordTypesToReturn'] = [
  'country',
  'state',
  'city',
  'area',
  'poi',
  'trail',
  'guide',
  'filter',
  'list'
];

export type AlgoliaSearchBoxProps = {
  configs: AlgoliaConfigs;
  getCurrentLatLng?: () => { lat: number; lng: number };
  hiddenResults?: string[];
  navigateOnSelect?: boolean;
  numResults?: number;
  onAddressSelect?: OnSelect<StreetAddressResult>;
  onNaturalLanguageResultSelect?: (text: string, index: number, selectionType: SearchSuggestionSelectionType, searchOrigin?: SearchOrigin) => void;
  onNearbyClick?: () => void;
  onResultSelect?: OnSelect<AlgoliaSearchResult>;
  onCoordinatesSelect?: OnSelect<CoordinatesResult>;
  // enables coordinate search (radius for nearby results)
  defaultRadiusMeters?: number;
  onSearch?: (response?: { queryID?: string }) => void;
  searchOrigin?: SearchOrigin;
  searchTypes: 'all' | Exclude<AlgoliaResultType, 'map' | 'track'>[] | ('map' | 'track')[];
  suppressAlgoliaAnalytics?: boolean;
  searchRequestLoading?: boolean;
  searchTabLocation?: SearchTabLocation;
  /**
   * Controls whether search results are fetched from the suggestions API (when true) or directly from Algolia (when false).
   * We should prefer to enable this where possible, but not all entity types are supported by the suggestions API.
   */
  useTypeAhead?: boolean;
} & Pick<
  CustomSearchBoxProps<AlgoliaSearchResult>,
  | 'className'
  | 'clearOnSelect'
  | 'debounceMs'
  | 'dropdownVariant'
  | 'onInputBlur'
  | 'onInputFocus'
  | 'placeholder'
  | 'size'
  | 'tertiaryElement'
  | 'testId'
  | 'variant'
  | 'value'
>;

const searchAll = (searchTypes: AlgoliaSearchBoxProps['searchTypes']) => searchTypes === 'all' || searchTypes.length === 0;
const nearbyResult: NearbyResult = { id: 'nearby', type: 'nearby', searchType: 'nearby' };

const removeHiddenHits = (hits: Hit[], hiddenObjectIds?: string[]) => {
  if (hiddenObjectIds?.length) {
    return hits.filter(hit => hit && 'objectID' in hit && !hiddenObjectIds.includes(hit.objectID));
  }
  return hits;
};

const AlgoliaSearchBox = ({
  configs,
  getCurrentLatLng,
  hiddenResults,
  navigateOnSelect = false,
  numResults = 50,
  onAddressSelect,
  onInputFocus: onInputFocusProp,
  onNaturalLanguageResultSelect,
  defaultRadiusMeters,
  onNearbyClick,
  onResultSelect,
  onCoordinatesSelect,
  onSearch,
  searchOrigin,
  searchTypes = 'all',
  suppressAlgoliaAnalytics,
  searchRequestLoading,
  searchTabLocation,
  useTypeAhead = false,
  ...customSearchBoxProps
}: AlgoliaSearchBoxProps) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const displayMetric = useSelector((state: { context: Context }) => state.context?.displayMetric);
  const experiments = useExperiments();
  const sponsoredListsEnabled = experiments['web-disco-sponsored-lists-search']?.value === 'on';
  const isMobile = useIsMobileSizedScreen();
  const { getRecentAlgoliaHits, addRecentAlgoliaHit } = useRecentAlgoliaSearches();
  // Holding off on tracking recent address searches via local storage due to privacy concerns
  // const { getRecentStreetAddressResults, addRecentStreetAddressResult } = useRecentStreetAddressSearches();
  const showNearbyResult = useMemo(() => !!onNearbyClick, [onNearbyClick]);
  const [results, setResults] = useState<Results<Result>>(showNearbyResult ? [nearbyResult] : []);
  const [activeTab, setActiveTab] = useState(locationTabId);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const baseUrl = usePublicBaseUrl();

  const index = useAlgoliaIndex(configs, searchTypes);
  const [fetch] = useLazyGetSuggestionsQuery();
  const queryAlgoliaDirectly = useMemo(() => {
    if (!useTypeAhead) {
      return true;
    }

    // properties not yet supported by our suggestions API
    if (
      Array.isArray(searchTypes) &&
      (searchTypes as ('map' | 'track' | 'alert' | 'attribute')[]).some(type => ['map', 'track', 'alert', 'attribute'].includes(type))
    ) {
      return true;
    }

    return false;
  }, [searchTypes, useTypeAhead]);

  // https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/
  const facetFilters = useMemo(() => {
    const filters: string[][] = [];

    if (searchAll(searchTypes)) {
      filters.push(['type:-track', 'type:-map']);
    } else {
      const typedSearchTypes = searchTypes as AlgoliaResultType[]; // This gets type to never[] since there isn't overlap between the constant possibilities
      if (typedSearchTypes.length === 1) {
        filters.push([`type:${searchTypes[0]}`]);
      } else {
        filters.push(typedSearchTypes.map(type => `type:${type}`));
      }
    }

    filters.push(['type:-photospot', 'subtype:-photospot']);

    return filters;
  }, [searchTypes]);

  const updateLocationResults = useCallback(
    (searchType: SearchType, includeNearby: boolean, hits: Hit[], options?: { hitsHeader?: string; coordinatesResult?: CoordinatesResult }) => {
      const filteredHits = removeHiddenHits(
        hits.filter(hit => Boolean(hit)),
        hiddenResults
      );
      let filteredResults: Result[] = filteredHits.map(hit =>
        hit.type === 'nls_query' ? { ...hit, id: hit.name } : algoliaHitToResult(hit, searchType)
      );
      // If the first result is an area with guides, remove guides for that area from the remaining list of results
      // and move them immediately below the area result
      const includeGuides = searchAll(searchTypes) || (searchTypes as AlgoliaResultType[]).includes('guide');
      const includeSponsoredLists =
        sponsoredListsEnabled && (searchAll(searchTypes) || (searchTypes as AlgoliaResultType[]).includes('sponsored_list'));
      filteredResults = reorderResults({ results: filteredResults, includeGuides, includeSponsoredLists, searchType });
      const newResults: Results<Result> = [];
      if (includeNearby && showNearbyResult) {
        newResults.push(nearbyResult);
      }
      if (hits.length || options?.coordinatesResult) {
        if (options?.hitsHeader) {
          newResults.push({ text: options.hitsHeader });
        }
        if (options?.coordinatesResult) {
          newResults.push(options.coordinatesResult);
        }
        newResults.push(...filteredResults);
      }
      setResults(newResults);
    },
    [hiddenResults, searchTypes, showNearbyResult, sponsoredListsEnabled]
  );

  const recentSearchesHeader = useMemo(() => intl.formatMessage({ defaultMessage: 'Recent searches' }), [intl]);

  const getLatLng = useCallback((term: string) => {
    const termTrim = term.replace(/[()]/g, '').trim();
    const latLng = termTrim.split(',');
    const lat = parseFloat(latLng[0]);
    const lng = parseFloat(latLng[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }

    return [lat, lng] as [number, number];
  }, []);

  const fetchLocationResults = useCallback(
    async (query: string) => {
      setCurrentQuery(query);
      const baseOptions: SearchRequestOptions = {
        hitsPerPage: numResults,
        facetFilters,
        ...(suppressAlgoliaAnalytics ? {} : { analyticsTags: getAnalyticsTags({ user, isMobile, searchOrigin, languageRegionCode, query }) })
      };

      const searchOptions: SearchRequestOptions = {
        ...baseOptions,
        getRankingInfo: true,
        aroundLatLngViaIP: true,
        // rank uniformly based on geo in these bands: 0-50mi, 50-200mi, 200+
        aroundPrecision: [
          { from: 0, value: 80000 },
          { from: 80000, value: 320000 },
          { from: 320000, value: 50000000 }
        ],
        aroundRadius: 'all'
      };

      if (query) {
        const latLng = getLatLng(query);
        if (latLng && defaultRadiusMeters && latLng.length === 2) {
          const latLngSearchOptions: SearchRequestOptions = {
            ...baseOptions,
            getRankingInfo: true,
            aroundLatLng: latLng.join(','),
            aroundRadius: defaultRadiusMeters
          };
          index
            .search('', latLngSearchOptions)
            .then(response => {
              if (onSearch) {
                onSearch({ queryID: response.queryID });
              }
              const coordinatesResult: CoordinatesResult = {
                type: 'coordinates' as const,
                id: query,
                latitude: latLng[0],
                longitude: latLng[1]
              };
              updateLocationResults('suggestion', false, response.hits, { coordinatesResult });
            })
            .catch(e => {
              logError(e);
              updateLocationResults('suggestion', false, []);
            });
        } else {
          index
            .search(query, searchOptions)
            .then(response => {
              if (onSearch) {
                onSearch({ queryID: response.queryID });
              }
              updateLocationResults('suggestion', false, response.hits);
            })
            .catch(e => {
              logError(e);
              updateLocationResults('suggestion', false, []);
            });
        }
      } else {
        const recentHits = getRecentAlgoliaHits(searchTypes);
        if (recentHits.length) {
          index
            .getObjects(recentHits.map(hit => hit.objectID))
            .then(response => {
              updateLocationResults('recent', true, response.results as AlgoliaHit[], { hitsHeader: recentSearchesHeader });
            })
            .catch(e => {
              logError(e);
              updateLocationResults('recent', true, []);
            });
        } else {
          updateLocationResults('nearby', true, []);
        }
      }
    },
    [
      facetFilters,
      getRecentAlgoliaHits,
      index,
      isMobile,
      languageRegionCode,
      numResults,
      defaultRadiusMeters,
      getLatLng,
      onSearch,
      recentSearchesHeader,
      searchOrigin,
      searchTypes,
      suppressAlgoliaAnalytics,
      updateLocationResults,
      user
    ]
  );

  const naturalLanguageSearchEnabled = Boolean(onNaturalLanguageResultSelect);

  const fetchLocationSuggestions = useCallback(
    async (query: string) => {
      setCurrentQuery(query);
      if (query) {
        const recordTypesToReturn: SuggestionsParams['recordTypesToReturn'] = [];
        if (searchTypes === 'all') {
          recordTypesToReturn.push(...defaultRecordTypes);
          if (naturalLanguageSearchEnabled) {
            recordTypesToReturn.push('nls_query');
          }
          if (sponsoredListsEnabled) {
            recordTypesToReturn.push('sponsored_list');
          }
        } else {
          searchTypes.forEach(searchType => {
            if (searchType === 'place') {
              recordTypesToReturn.push('city', 'state', 'country');
            } else {
              recordTypesToReturn.push(searchType);
            }
          });
        }

        const result = await fetch({
          query,
          limit: numResults,
          localeUnits: displayMetric ? 'metric' : 'imperial',
          ...(recordTypesToReturn.length > 0 ? { recordTypesToReturn } : {})
        }).unwrap();
        if (onSearch) {
          onSearch();
        }
        updateLocationResults('suggestion', false, result.searchResults);
      } else {
        const recentHits = getRecentAlgoliaHits(searchTypes);
        if (recentHits.length) {
          index
            .getObjects(recentHits.map(hit => hit.objectID))
            .then(response => {
              updateLocationResults('recent', true, response.results as AlgoliaHit[], { hitsHeader: recentSearchesHeader });
            })
            .catch(e => {
              logError(e);
              updateLocationResults('recent', true, []);
            });
        } else {
          updateLocationResults('nearby', true, []);
        }
      }
    },
    [
      displayMetric,
      fetch,
      getRecentAlgoliaHits,
      index,
      naturalLanguageSearchEnabled,
      numResults,
      onSearch,
      recentSearchesHeader,
      searchTypes,
      sponsoredListsEnabled,
      updateLocationResults
    ]
  );

  const fetchStreetAddressResults = useCallback(
    async (query: string) => {
      setCurrentQuery(query);

      if (query) {
        const latLng = getCurrentLatLng?.();
        const lat = latLng?.lat;
        const lng = latLng?.lng;
        const { searchResults } = await getStreetAddressResults(query, lat, lng);
        setResults(
          searchResults.map<StreetAddressResult>(result => {
            const { latitude, longitude } = result.geo;
            return { ...result, id: [longitude, latitude].join(',') };
          })
        );
      } else {
        setResults([]);
      }
    },
    [getCurrentLatLng]
  );

  const fetchResultsRequest = useMemo(() => {
    if (activeTab === streetAddressTabId) {
      return fetchStreetAddressResults;
    }
    return queryAlgoliaDirectly ? fetchLocationResults : fetchLocationSuggestions;
  }, [activeTab, fetchLocationResults, fetchLocationSuggestions, fetchStreetAddressResults, queryAlgoliaDirectly]);

  const onGuideSelect = useCallback(
    (guide: GuideMetadata, index: number, searchType?: SearchType) => {
      logGuideClicked({
        guide_id: String(guide.ID),
        source_type: 'search',
        horizontal_index: index,
        vertical_index: 1 // The guides carousel always renders as the 2nd element
      });

      const result = guideMetadataToSearchResult(guide, searchType);
      if (onResultSelect) {
        onResultSelect(result);
      }
      if (navigateOnSelect) {
        window.location.assign(`${baseUrl}${getUrl(result, languageRegionCode, true)}`);
      }
    },
    [baseUrl, languageRegionCode, navigateOnSelect, onResultSelect]
  );

  const onSponsoredListSelect = useCallback(
    (list: SponsoredListHit, index: number, searchType?: SearchType) => {
      if (searchOrigin) {
        logSearchResultClicked({
          click_position: index,
          content_id: list.objectID,
          item_type: 'sponsored list',
          location: searchOrigin,
          selection_type: getSearchSelectionType(searchType)
        });
      }
      const result = algoliaHitToResult(list, searchType);
      if (onResultSelect) {
        onResultSelect(result);
      }
      if (navigateOnSelect) {
        window.location.assign(`${baseUrl}${getUrl(result, languageRegionCode, true)}`);
      }
    },
    [baseUrl, languageRegionCode, navigateOnSelect, onResultSelect, searchOrigin]
  );

  const onSelect: OnSelect<Result> = useCallback(
    (result, additionalInfo) => {
      if (!result) {
        return;
      }
      if (result.type === 'nearby') {
        onNearbyClick?.();
      } else if (result.type === 'nls_query') {
        onNaturalLanguageResultSelect?.(result.name, additionalInfo?.index ?? 0, 'NLS open query', searchOrigin);
      } else if (result.type === 'guides' || result.type === 'sponsored-lists') {
        // do nothing
      } else if (result.type === 'address') {
        // addRecentStreetAddressResult(result);
        if (searchOrigin) {
          logSearchResultClicked({
            click_position: additionalInfo?.index,
            item_type: 'address',
            location: searchOrigin,
            query_length: additionalInfo?.query?.length,
            query_text: additionalInfo?.query,
            selection_type: 'search suggestion'
          });
        }
        onAddressSelect?.(result, additionalInfo);
      } else if (result.type === 'coordinates') {
        onCoordinatesSelect?.(result, additionalInfo);
      } else {
        addRecentAlgoliaHit({ objectID: result.objectID, type: result.type });
        if (searchOrigin) {
          logSearchResultClicked({
            click_position: additionalInfo?.index,
            content_id: result.id,
            item_type: getSearchItemType(result.type),
            location: searchOrigin,
            poi_type_raw: result.type === 'poi' ? result.subtype : undefined,
            query_length: additionalInfo?.query?.length,
            query_text: additionalInfo?.query,
            selection_type: getSearchSelectionType(result.searchType)
          });
        }
        if (result.type === 'guide') {
          logGuideClicked({
            guide_id: String(result.ID),
            source_type: 'search',
            vertical_index: additionalInfo?.index
          });
        }
        if (result.type === 'page' && result.subtype === 'guides') {
          logGuideIndexClicked({
            guide_index_location: 'search'
          });
        }
        if (onResultSelect) {
          onResultSelect(result, additionalInfo);
        }
        if (navigateOnSelect && typeof window !== 'undefined') {
          window.location.assign(`${baseUrl}${getUrl(result, languageRegionCode, true)}`);
        }
      }
    },
    [
      onNearbyClick,
      onNaturalLanguageResultSelect,
      searchOrigin,
      onAddressSelect,
      onCoordinatesSelect,
      addRecentAlgoliaHit,
      onResultSelect,
      navigateOnSelect,
      baseUrl,
      languageRegionCode
    ]
  );

  const onInputFocus = useCallback(() => {
    if (searchOrigin) {
      const { href, pathname } = window.location;
      logExploreSearchBoxClicked({ search_origin: searchOrigin, href, path: pathname });
    }
    onInputFocusProp?.();
  }, [onInputFocusProp, searchOrigin]);

  const searchResultsTabBar = useMemo<SearchResultsTabBar | undefined>(() => {
    if (onAddressSelect) {
      return {
        activeTab,
        onChange: id => {
          logSearchTabSwitched({
            search_tab_location: searchTabLocation,
            to_tab: id === streetAddressTabId ? 'street address' : 'location'
          });
          setActiveTab(id);
        },
        tabs: [
          { id: locationTabId, label: intl.formatMessage({ defaultMessage: 'Location' }), testId: `search-tab-${locationTabId}` },
          { id: streetAddressTabId, label: intl.formatMessage({ defaultMessage: 'Street address' }), testId: `search-tab-${streetAddressTabId}` }
        ]
      };
    }
    return undefined;
  }, [activeTab, intl, searchTabLocation, onAddressSelect]);

  const renderResultContent = useCallback(
    (result: Result) => {
      switch (result.type) {
        case 'trail':
          return <TrailResultContent query={currentQuery} trail={result} />;
        case 'nearby':
          return <NearbyResultContent />;
        case 'nls_query':
          return <NLSearchResultContent html={result.name_with_formatting} />;
        case 'guides':
          return <AlgoliaGuidesCarousel onGuideSelect={onGuideSelect} result={result} />;
        case 'sponsored-lists':
          return <SponsoredListsCarousel className={styles.carousel} onSponsoredListSelect={onSponsoredListSelect} result={result} />;
        case 'address':
          return <StreetAddressResultContent query={currentQuery} result={result} />;
        case 'coordinates':
          return <CoordinatesResultContent result={result} />;
        default:
          return (
            <AlgoliaSearchResultContent
              hideResultType={Array.isArray(searchTypes) && searchTypes.length === 1}
              result={result}
              url={`${baseUrl}${getUrl(result, languageRegionCode, true)}`}
              query={currentQuery}
            />
          );
      }
    },
    [baseUrl, currentQuery, languageRegionCode, onGuideSelect, onSponsoredListSelect, searchTypes]
  );

  const renderAdditionalContent = useCallback<NonNullable<ComponentProps<typeof CustomSearchBox>['renderAdditionalContent']>>(
    (query, hideResults) => {
      if (!query && naturalLanguageSearchEnabled) {
        return (
          <NLSearchSuggestions
            onSelect={(text, index) => {
              onNaturalLanguageResultSelect?.(text, index, 'suggested prompt', searchOrigin);
              hideResults();
            }}
            searchOrigin={searchOrigin}
          />
        );
      }
      return null;
    },
    [naturalLanguageSearchEnabled, onNaturalLanguageResultSelect, searchOrigin]
  );

  const emptyState = useMemo<SearchResultsEmptyState | undefined>(() => {
    if (onAddressSelect) {
      if (activeTab === streetAddressTabId) {
        return {
          primary: <FormattedMessage defaultMessage="Search by address" />,
          secondary: <FormattedMessage defaultMessage="Find trails near home, a hotel, or any address" />
        };
      }
      return {
        primary: <FormattedMessage defaultMessage="Search by location" />,
        secondary: <FormattedMessage defaultMessage="Search by city, park, or trail name" />
      };
    }
    return undefined;
  }, [activeTab, onAddressSelect]);

  return (
    <CustomSearchBox
      {...customSearchBoxProps}
      emptyState={emptyState}
      fetchResultsRequest={fetchResultsRequest}
      inputMaxLength={naturalLanguageSearchEnabled ? 400 : undefined} // Don't want to let users send massive text strings for nls
      onInputFocus={onInputFocus}
      onSelect={onSelect}
      renderAdditionalContent={renderAdditionalContent}
      renderResultContent={renderResultContent}
      results={results}
      searchResultsTabBar={searchResultsTabBar}
      searchRequestLoading={searchRequestLoading}
    />
  );
};

export default AlgoliaSearchBox;
